import { isProductionDomain } from '@ptml/root-config';
import type { LifeCycles } from 'single-spa';

const isPublicEnvironment = (): boolean =>
    isProductionDomain() ||
    [
        'intsurepart.testpartstrader.us.com',
        'supplieruatinsurer.testpartstrader.us.com',
        'estimaticsurepart.testpartstrader.us.com',
        'estimaticinsurer.testpartstrader.us.com',
        'trainingsurepart.testpartstrader.us.com',
        'traininginsurer.testpartstrader.us.com',
    ].includes(globalThis.location.hostname);

/**
 * Helper functions for managing import maps and module loading
 */

/**
 * Gets the current import map from the document
 * @returns The import map object with imports record
 */
export const getImportMap = (): { imports: Record<string, string> } =>
    JSON.parse(document.querySelector('script[type=importmap]')?.textContent ?? '{}') as {
        imports: Record<string, string>;
    };

/**
 * Injects the import-map-overrides script for internal environments
 */
export const injectImportMapOverrides = (): void => {
    // Manually add the import map script for internal environments
    if (!isPublicEnvironment()) {
        const script = document.createElement('script');

        script.type = 'text/javascript';
        script.src =
            'https://cdn.jsdelivr.net/npm/import-map-overrides@6.0.0/dist/import-map-overrides.min.js';
        script.fetchPriority = 'low';
        script.async = true;
        script.defer = true;

        document.head.append(script);
    }
};

/**
 * Adds the import map override dev tools to the page
 */
export const addImportMapOverrideDevTools = (): void => {
    const importMapOverrideDevTools = document.createElement('import-map-overrides-full');
    importMapOverrideDevTools.setAttribute('dev-libs', 'dev-libs');
    document.body.append(importMapOverrideDevTools);
};

/**
 * Dev only function to inject the vite refresh snippet into the page.
 * @param baseUrl The base URL for the vite refresh
 */
export const injectViteRefreshSnippet = (baseUrl: string): void => {
    const scriptElement = document.createElement('script');
    scriptElement.type = 'module';
    scriptElement.textContent = `
            import { injectIntoGlobalHook } from "${baseUrl}/@react-refresh";

            injectIntoGlobalHook(window);
            window.$RefreshReg$ = () => {};
            window.$RefreshSig$ = () => (type) => type;
        `;
    document.head.append(scriptElement);
};

/**
 * Creates a loadApp function that handles module loading with import map overrides and HMR
 * @param importMap The import map containing module mappings
 * @returns A function that loads applications by name
 */

/**
 * Creates a loadApp function that returns single-spa LifeCycles for the given application name.
 * Typed to satisfy constructApplications expectation (Promise<LifeCycles<any>>), resolving TS2322.
 */
export const createLoadAppFunction =
    (importMap: {
        imports: Record<string, string>;
    }): ((args: { name: string }) => Promise<LifeCycles<Record<string, unknown>>>) =>
    ({ name }: { name: string }): Promise<LifeCycles<Record<string, unknown>>> => {
        const pathToApp: string =
            localStorage.getItem(`import-map-override:${name}`) ?? importMap.imports[name];
        // Only inject the Vite HMR refresh snippet for dev-server bundles (served as .tsx source).
        // Preview-served bundles are plain .js files and have no /@react-refresh endpoint —
        // attempting to fetch it causes a module load error that prevents the MFE from mounting.
        if (pathToApp.includes('http://localhost') && pathToApp.endsWith('.tsx')) {
            injectViteRefreshSnippet(new URL(pathToApp).origin);
        }
        // Dynamic import returns the module whose shape conforms to single-spa LifeCycles (bootstrap/mount/unmount etc.)
        return import(/* @vite-ignore */ pathToApp) as Promise<LifeCycles<Record<string, unknown>>>;
    };

/**
 * Initializes all import map related functionality
 * @returns An object containing the import map and loadApp function
 */
export const initializeImportMap = (): {
    importMap: { imports: Record<string, string> };
    loadApp: (args: { name: string }) => Promise<LifeCycles<Record<string, unknown>>>;
} => {
    const importMap = getImportMap();

    if (!__TESTING__ && !isPublicEnvironment()) {
        injectImportMapOverrides();
        addImportMapOverrideDevTools();
    }

    const loadApp = createLoadAppFunction(importMap);

    return {
        importMap,
        loadApp,
    };
};
